import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { ArrowLeft, Printer, CreditCard, Plus, CheckCircle, DeviceMobile } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { StatusBadge } from "@/components/StatusBadge";
import PaymentDialog from "@/components/PaymentDialog";
import MobileMoneyDialog from "@/components/MobileMoneyDialog";
import { Button } from "@/components/ui/button";

const METHOD_LABELS = {
  cash: "Cash", mtn_money: "MTN Mobile Money", airtel_money: "Airtel Money",
  bank_transfer: "Bank Transfer", stripe_card: "Card (Stripe)",
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [momoOpen, setMomoOpen] = useState(false);
  const [payingOnline, setPayingOnline] = useState(false);
  const { fmt, currency, settings } = useCurrency();

  const load = () => api.get(`/invoices/${id}`).then(({ data }) => setInvoice(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [id]);

  const payOnline = async () => {
    setPayingOnline(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        invoice_id: id, currency, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setPayingOnline(false);
    }
  };

  if (!invoice)
    return <div className="flex h-40 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;

  const balance = invoice.total - invoice.amount_paid;

  return (
    <div className="space-y-6" data-testid="invoice-detail-page">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate("/invoices")} data-testid="back-to-invoices"
          className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground">
          <ArrowLeft size={16} /> Back to invoices
        </button>
        <div className="flex gap-2">
          {invoice.status !== "paid" && (
            <>
              <Button variant="outline" onClick={() => setPayOpen(true)} data-testid="record-payment-button"
                className="transition-colors hover:border-primary hover:text-primary">
                <Plus size={15} className="mr-1.5" /> Record payment
              </Button>
              <Button onClick={payOnline} disabled={payingOnline} data-testid="pay-online-button" className="transition-colors">
                <CreditCard size={15} className="mr-1.5" /> {payingOnline ? "Redirecting…" : `Pay by card (${currency})`}
              </Button>
              <Button variant="outline" onClick={() => setMomoOpen(true)} data-testid="pay-mobile-money-button"
                className="transition-colors hover:border-primary hover:text-primary">
                <DeviceMobile size={15} className="mr-1.5" /> Mobile money
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => window.print()} data-testid="print-invoice-button" className="transition-colors">
            <Printer size={15} className="mr-1.5" /> Print
          </Button>
        </div>
      </div>

      <div id="print-area" className="mx-auto max-w-3xl border border-border bg-card p-10">
        <div className="flex items-start justify-between border-b-2 border-foreground pb-6">
          <div>
            <div className="font-heading text-2xl font-bold">{settings?.property_name || "EasyStay Lodge"}</div>
            <div className="mt-1 text-xs text-muted-foreground">{settings?.address || "Lusaka, Zambia"}</div>
            <div className="text-xs text-muted-foreground">{settings?.phone}</div>
          </div>
          <div className="text-right">
            <div className="font-heading text-3xl font-bold text-primary">INVOICE</div>
            <div className="font-heading text-lg font-bold" data-testid="invoice-number">{invoice.number}</div>
            <div className="text-xs text-muted-foreground">Issued {format(new Date(invoice.issued_at), "dd MMM yyyy")}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 py-6 text-sm">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Billed to</div>
            <div className="mt-1 font-medium" data-testid="invoice-guest-name">{invoice.guest_name}</div>
            {invoice.booking && <div className="text-xs text-muted-foreground">{invoice.booking.guest_phone}</div>}
          </div>
          <div className="text-right">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Stay</div>
            <div className="mt-1">{invoice.check_in} → {invoice.check_out}</div>
            <div className="mt-2"><StatusBadge status={invoice.status} testId="invoice-status-badge" /></div>
          </div>
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-foreground text-left text-xs uppercase tracking-wider">
              <th className="py-2 font-medium">Description</th>
              <th className="py-2 text-center font-medium">Qty</th>
              <th className="py-2 text-right font-medium">Unit</th>
              <th className="py-2 text-right font-medium">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((item, i) => (
              <tr key={item.description} className="border-b border-border" data-testid={`invoice-item-${i}`}>
                <td className="py-3">{item.description}</td>
                <td className="py-3 text-center">{item.qty}</td>
                <td className="py-3 text-right">{fmt(item.unit_price)}</td>
                <td className="py-3 text-right font-medium">{fmt(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 flex justify-end">
          <div className="w-64 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span data-testid="invoice-subtotal">{fmt(invoice.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">VAT ({invoice.vat_rate}%)</span><span data-testid="invoice-vat">{fmt(invoice.vat_amount)}</span></div>
            <div className="flex justify-between border-t border-foreground pt-2 font-heading text-lg font-bold">
              <span>Total</span><span data-testid="invoice-total">{fmt(invoice.total)}</span>
            </div>
            <div className="flex justify-between text-status-confirmed"><span>Paid</span><span data-testid="invoice-paid">{fmt(invoice.amount_paid)}</span></div>
            <div className="flex justify-between font-semibold"><span>Balance</span><span data-testid="invoice-balance">{fmt(balance)}</span></div>
            {currency !== "ZMW" && (
              <div className="pt-1 text-right text-[11px] text-muted-foreground">Amounts shown in {currency}; billed in ZMW.</div>
            )}
          </div>
        </div>

        {invoice.status === "paid" && (
          <div className="mt-6 flex items-center gap-2 border border-status-confirmed/40 bg-status-confirmed/10 px-4 py-3 text-sm font-medium text-status-confirmed" data-testid="invoice-paid-banner">
            <CheckCircle size={18} weight="fill" /> Paid in full — thank you.
          </div>
        )}

        <div className="mt-10 border-t border-border pt-4 text-[11px] text-muted-foreground">
          Generated by EasyStay Manager · Powered towards EasyStay guest platform sync
        </div>
      </div>

      <div className="mx-auto max-w-3xl border border-border bg-card p-6" data-testid="payments-history">
        <h2 className="font-heading text-lg font-bold">Payment history</h2>
        {invoice.payments.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground" data-testid="payments-empty">No payments recorded yet.</div>
        ) : (
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-2 font-medium">Date</th>
                <th className="p-2 font-medium">Method</th>
                <th className="p-2 font-medium">Reference</th>
                <th className="p-2 text-right font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {invoice.payments.map((p, i) => (
                <tr key={`${p.reference}-${p.paid_at}`} className="border-b border-border last:border-0" data-testid={`payment-row-${i}`}>
                  <td className="p-2">{format(new Date(p.paid_at), "dd MMM yyyy HH:mm")}</td>
                  <td className="p-2">{METHOD_LABELS[p.method] || p.method}</td>
                  <td className="p-2 text-muted-foreground">{p.reference || "—"}</td>
                  <td className="p-2 text-right font-medium">{fmt(p.amount_zmw)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} invoice={invoice} onSaved={load} />
      <MobileMoneyDialog open={momoOpen} onOpenChange={setMomoOpen} invoice={invoice} onPaid={load} />
    </div>
  );
}
